import { useMutation } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { Field, Modal, ScaledInput, useToast } from "@cx/ui";
import type { PriceListFormValues, PriceListView } from "@cx/contracts";
import { toPct4 } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { KIND_LABEL, pctNumber } from "./labels";

type Values = {
  name: string;
  kind: PriceListView["kind"];
  ratePct4: number | null;
  base: PriceListView["base"];
  validFrom: string;
  validTo: string;
  active: boolean;
};

export const EMPTY_PRICE_LIST: Values = {
  name: "",
  kind: "rate",
  ratePct4: null,
  base: "price",
  validFrom: "",
  validTo: "",
  active: true,
};

export const toValues = (l: PriceListView): Values => ({
  name: l.name,
  kind: l.kind,
  ratePct4: l.ratePct4 ?? null,
  base: l.base,
  validFrom: l.validFrom ?? "",
  validTo: l.validTo ?? "",
  active: l.active,
});

/** Cabeçalho da lista de preço (RF-CAD-04): nome, tipo, percentual, base, vigência e situação. */
export function PriceListDialog({
  id,
  initial,
  onClose,
  onSaved,
}: {
  id?: string;
  initial: Values;
  onClose(): void;
  onSaved(id: string): void;
}): ReactElement {
  const { api, me } = useSession();
  const toast = useToast();
  const [v, setV] = useState(initial);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Base custo só para quem vê custo (a API recusa com 403); quem não vê mantém a opção atual visível.
  const canCost = can(me.permissions as Permissions, "products", "read_cost") || initial.base === "cost";
  const save = useMutation({
    mutationFn: () => {
      const body: PriceListFormValues = {
        name: v.name,
        kind: v.kind,
        ratePct4: v.kind === "custom" ? null : v.ratePct4,
        base: v.base,
        validFrom: v.validFrom || null,
        validTo: v.validTo || null,
        active: v.active,
      };
      return api<{ id: string } | null>(id ? `/price-lists/${id}` : "/price-lists", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (r) => {
      toast("success", "Lista de preço salva");
      onSaved(id ?? r!.id);
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_price_list") setErrors({ name: e.message });
      toast("danger", errorMessage(e));
    },
  });
  const set = <K extends keyof Values>(k: K, x: Values[K]) => setV((o) => ({ ...o, [k]: x }));
  return (
    <Modal
      open={open}
      title={id ? "Editar lista de preço" : "Nova lista de preço"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input value={v.name} maxLength={60} data-autofocus onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Tipo" required className="col-md-6" error={errors.kind}>
          <select
            className="form-select"
            value={v.kind}
            onChange={(e) => set("kind", e.target.value as Values["kind"])}
          >
            {(Object.keys(KIND_LABEL) as Values["kind"][]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Base" className="col-md-6" error={errors.base}>
          <select
            className="form-select"
            value={v.base}
            onChange={(e) => set("base", e.target.value as Values["base"])}
          >
            <option value="price">Preço de venda</option>
            {canCost && <option value="cost">Preço de custo</option>}
          </select>
        </Field>
        {v.kind !== "custom" && (
          <Field
            label="Percentual"
            required
            className="col-md-6"
            error={errors.ratePct4}
            hint="Positivo = acréscimo; negativo = desconto (ex.: -10)."
          >
            <ScaledInput
              value={v.ratePct4}
              parse={toPct4}
              format={pctNumber}
              suffix="%"
              onChange={(p) => set("ratePct4", p)}
            />
          </Field>
        )}
        {v.kind === "custom" && (
          <p className="col-12 form-text">
            Na lista customizada o preço (ou o percentual sobre a base) é informado em cada produto, depois de
            salvar.
          </p>
        )}
        <Field
          label="Vigência de"
          required={v.kind === "period"}
          className="col-md-6"
          error={errors.validFrom}
        >
          <input type="date" value={v.validFrom} onChange={(e) => set("validFrom", e.target.value)} />
        </Field>
        <Field
          label="Vigência até"
          required={v.kind === "period"}
          className="col-md-6"
          error={errors.validTo}
        >
          <input type="date" value={v.validTo} onChange={(e) => set("validTo", e.target.value)} />
        </Field>
      </div>
      <div className="form-check">
        <input
          id="pl-active"
          type="checkbox"
          className="form-check-input"
          checked={v.active}
          onChange={(e) => set("active", e.target.checked)}
        />
        <label className="form-check-label" htmlFor="pl-active">
          Ativa
        </label>
      </div>
    </Modal>
  );
}
